import validator from 'validator';
import { AttachmentType } from '../../../../../shared/editor/types';
import {
  EmailContentSizeAllowed,
  SubjectSizeAllowed,
} from '../../../../../shared/utils';
import {
  checkSubjectEmailSuggestions,
  checkTotalEmailContentSizeValidator,
  getMaximumCharacterCountFromSpinText,
  regexForSenderSignatureCountCheck,
} from './helpers';
import { extractTags } from './components/sidebar/tabs/utils/get-variable-tag-mapping-with-values';
import { stripChipTagsFromHtml } from '../../../../../shared/editor/helpers';
import { convertHtmlToText } from './components/sidebar/tabs/utils/get-parsed-content-for-text-only-email';

const validateContent = ({
  value,
  attachments,
  attachmentsSizeInBytes,
  t,
  noMergeAndVariableTagsAllowed = false,
  isExternalEmail = false,
}) => {
  if (
    validator.isEmpty(value.split('&nbsp;').join(' ').trim()) &&
    attachments.length === 0
  ) {
    return 'Content should not be empty';
  }

  if (value?.includes('error-chip-container')) {
    return 'Email body cannot contain error tags.';
  }

  if (noMergeAndVariableTagsAllowed || isExternalEmail) {
    const addedTags = extractTags(value);
    if (addedTags.length > 0) {
      return noMergeAndVariableTagsAllowed
        ? "Merge tags are unavailable when sending email to multiple recipients in 'To'"
        : 'Merge tags are unavailable when sending email to external recipients';
    }
  }

  // Regex for counting total occurrences
  const occurrences = (value.match(regexForSenderSignatureCountCheck) || [])
    .length; // Counts the number of matches
  if (occurrences > 1) {
    return '{{SenderSignature}} can not be used more than once.';
  }

  if (
    checkTotalEmailContentSizeValidator({
      content: value,
      attachmentsSizeInBytes,
    })
  ) {
    const sizeInMb = Math.round(EmailContentSizeAllowed / 1024 / 1000);
    return `${t('messages.email_content_size_exceeds')} ${sizeInMb} MB.`;
  }

  return '';
};

export const validateEmail = ({ value }) => {
  const emails = value.split(',');

  if (emails.length <= 5) {
    if (!emails.every((email) => validator.isEmail(email.trim()))) {
      return 'Enter a valid email address';
    }
  } else {
    return 'Maximum of 5 emails are allowed';
  }

  return '';
};

type ValidateProps = {
  name: string;
  value: string;
  allowEmptySubject?: boolean;
  showTitle?: boolean;
  attachmentsSizeInBytes?: number;
  attachments: AttachmentType[];
  t: any;
  noMergeAndVariableTagsAllowed?: boolean;
  sendThisEmailInSameThreadCheckbox?: boolean;
  isExternalEmail?: boolean;
};

export const validate = ({
  name,
  value,
  allowEmptySubject,
  showTitle,
  attachmentsSizeInBytes,
  attachments,
  t,
  noMergeAndVariableTagsAllowed = false,
  sendThisEmailInSameThreadCheckbox = false,
  isExternalEmail = false,
}: ValidateProps): string => {
  if (
    name === 'title' &&
    showTitle &&
    validator.isEmpty(value.split('&nbsp;').join(' ').trim())
  ) {
    return 'This title field is required.';
  }

  if (name === 'subject') {
    if (!allowEmptySubject && validator.isEmpty(value.trim())) {
      return 'Subject line missing: First step of the sequence has to have a subject line.';
    }

    if (allowEmptySubject && validator.isEmpty(value.trim())) {
      if (sendThisEmailInSameThreadCheckbox) {
        return '';
      }

      return "Subject line can't be empty if sending email as a new thread";
    }

    if (
      checkSubjectEmailSuggestions(
        convertHtmlToText(stripChipTagsFromHtml(value)),
      )?.subjectLength > SubjectSizeAllowed
    ) {
      return 'Subject line is too long.';
    }

    if (value?.includes('error-chip-container')) {
      return 'Subject line cannot contain error tags.';
    }

    // Regex for counting total occurrences
    const occurrences = (value.match(regexForSenderSignatureCountCheck) || [])
      .length; // Counts the number of matches

    if (occurrences > 0) {
      return '{{SenderSignature}} can not be used in subject.';
    }

    return '';
  }
  if (name === 'preheader') {
    if (value?.includes('error-chip-container')) {
      return 'Preheader line cannot contain error tags.';
    }
  }

  if (name === 'content') {
    return validateContent({
      value,
      attachments,
      attachmentsSizeInBytes,
      t,
      noMergeAndVariableTagsAllowed,
      isExternalEmail,
    });
  }

  if (name === 'email') {
    return validateEmail({ value });
  }

  if (name === 'to') {
    if (!value || !validator.isEmail(value.trim())) {
      return 'Enter a valid email address';
    }
  }

  if (name === 'cc' || name === 'bcc') {
    if (value && !validator.isEmail(value.trim())) {
      return 'Enter a valid email address';
    }
  }

  return '';
};
